import { useMemo, useState } from 'react'
import { createSearchParams, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import Typography from '@mui/material/Typography'
import { FieldArray } from 'react-final-form-arrays'
import { Form, FormValues } from 'pagesNew/OrginizeBlogPages/OrganizeCreateSuccessStory/Form'
import { FIND_ONE_SUCCESS_STORY_SECTION, UPDATE_SUCCESS_STORY_SECTION } from 'queries/successStory'
import { PageLoader } from 'components/PageLoader'
import {
  findOneSuccessStorySection,
  findOneSuccessStorySectionVariables,
} from '__generatedTypes__/findOneSuccessStorySection'
import {
  updateSuccessStorySection,
  updateSuccessStorySectionVariables,
} from '__generatedTypes__/updateSuccessStorySection'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { QUERY_PARAM, SUCCESS_STORIES_TAB } from 'pagesNew/BlogPages'
import { InputField } from 'components/Input'
import { validateRequired, validateRequiredField } from 'utils/validation'
import { searchByStoryteller_searchByStoryteller_items as Storyteller } from '__generatedTypes__/searchByStoryteller'
import { toast } from 'utils/toast'
import { useBroadcastChannel } from 'hooks/useBroadcastChannel'
import { BROADCAST_CHANNEL } from 'constants/consts'
import { REFETCH_SUCCESS_STORIES_MESSAGE } from 'pagesNew/SuccessStory/Create'
import * as classes from '../OrganizeCreateSuccessStory/Form/styles'
import { SuccessStoriesFieldArray } from '../OrganizeCreateSuccessStory/Form/SuccessStoriesFieldArray'

type Props = {
  platform: Platform
}

export const OrganizeEditSuccessStory = withPlatformCheck(({ platform }: Props) => {
  const { id: sectionId } = useParams()
  const [isPlatformMatch, setIsPlatformMatch] = useState(true)
  const { navigate } = useLink()

  const { data, loading } = useQuery<findOneSuccessStorySection, findOneSuccessStorySectionVariables>(
    FIND_ONE_SUCCESS_STORY_SECTION,
    {
      variables: {
        findOneSuccessStorySectionId: sectionId || '',
      },
      skip: !sectionId,
      onCompleted: (data) => {
        const successStorSection = data.findOneSuccessStorySection
        setIsPlatformMatch(successStorSection?.platform.id === platform.id)
      },
    }
  )

  const [updateSuccessStorySection] = useMutation<updateSuccessStorySection, updateSuccessStorySectionVariables>(
    UPDATE_SUCCESS_STORY_SECTION
  )

  const handleSubmit = async (values: FormValues) => {
    try {
      const { id, title, description = '' } = values
      await updateSuccessStorySection({
        variables: {
          updateSuccessStorySectionInputFieldName: {
            id,
            title,
            description,
            platform: {
              id: platform.id,
            },
            successStory: values.articles.map(({ id }) => ({ id })),
            successStoryIds: values.articles.map(({ id }) => id),
          },
        },
      })

      navigate(
        cmsRoutes.BLOG_PAGES,
        {},
        { searchParams: createSearchParams({ [QUERY_PARAM]: SUCCESS_STORIES_TAB.id }) }
      )
      toast.success('Success story section updated successfully')
    } catch (error) {
      toast.error('Failed to update success story section')
    }
  }

  useBroadcastChannel(BROADCAST_CHANNEL, (event) => {
    if (event.data === REFETCH_SUCCESS_STORIES_MESSAGE) {
      toast.success('Success story created successfully')
    }
  })

  const initialValues: FormValues = useMemo(() => {
    const successStory = data?.findOneSuccessStorySection?.successStory || []

    const articlesOrder: FormValues['articles'] =
      (data?.findOneSuccessStorySection?.successStoryIds
        .map((id) => successStory.find(({ id: successStoryId }) => id === successStoryId))
        .filter((successStory) => !!successStory) as FormValues['articles']) || []

    return {
      id: sectionId,
      title: data?.findOneSuccessStorySection?.title || '',
      description: data?.findOneSuccessStorySection?.description || '',
      articles: articlesOrder,
    }
  }, [loading])

  if (loading) {
    return <PageLoader />
  }

  if (!isPlatformMatch) {
    return (
      <Typography>
        You are trying to edit success story section with different platform (platform in url: {platform.title}, success
        story platform: {data?.findOneSuccessStorySection?.platform?.title})
      </Typography>
    )
  }

  return (
    <Form
      platform={platform}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      title="Organize Success Stories"
      createLink={cmsRoutes.CREATE_SUCCESS_STORY}
      renderFields={({ form, linkParams }) => (
        <>
          <InputField
            css={classes.field}
            name="description"
            validate={validateRequired}
            label="Description"
            placeholder="Enter Description"
            fullWidth
          />
          <FieldArray<Storyteller> name="articles" validate={validateRequiredField}>
            {({ fields, meta }) => (
              <SuccessStoriesFieldArray
                platform={platform}
                onChange={form.change}
                fields={fields}
                meta={meta}
                linkParams={linkParams}
              />
            )}
          </FieldArray>
        </>
      )}
    />
  )
})
